import {
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";
import Avatar from "react-avatar";
import { useDispatch } from "react-redux";
import Webcam from "react-webcam";
import { v4 } from "uuid";

import { useToast } from "hooks/toast";

import { updateUser } from "store/modules/user/actions";
import { useAppSelector } from "store/rootReducer";

import api, { storageBaseUrl } from "services/api";

import CameraIcon from "assets/svg/camera-arquivos.svg";

import { LogOperation } from "utils/entities/logOperation";

import ModalSelectOrTakePicture from "./components/ModalSelectOrTakePicture";
import { BoxWebCamImage, ButtonTakePhoto, WebCamContainer } from "./styles";

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: "user",
};

export interface UploadImageRef {
  handleUploadImage: () => Promise<void>;
}

interface PhotoWebCamProps {
  containerName: string;
  onImageChange: () => void;
}

const PhotoWebCam = forwardRef<UploadImageRef, PhotoWebCamProps>(
  ({ containerName, onImageChange }, ref) => {
    const { t } = useTranslation();
    const [profileImage, setProfileImage] = useState<File | null>(null);
    const [isCameraMode, setIsCameraMode] = useState(false);
    const [showModalPicture, setShowModalPicture] = useState(false);
    const [preview, setPreview] = useState<string>("");
    const webcamRef = useRef<Webcam>(null);
    const dispatch = useDispatch();
    const { addToast } = useToast();

    const containerNameAux = containerName ?? "riograndedosul";

    const { user } = useAppSelector((state) => state.user);

    const { selectedVideo } = useAppSelector((state) => state.mediaDevices);

    const updateUserStorage = async (picture: string) => {
      if (user) {
        const newUser = {
          ...user,
          picture,
        };
        dispatch(updateUser(newUser));
      }
    };

    const handleUploadImage = async () => {
      if (!profileImage) return;

      try {
        const extension =
          profileImage.name.match(/\.[a-z0-9]{2,4}$/i)?.[0] || ".jpg";
        const filename = `${v4()}_${moment().format(
          "DDMMYYYYHHmmss",
        )}${extension}`;
        const filePath = `audiencias\\${new Date().getFullYear()}\\unidade_${LogOperation.getCustomerId()}\\photo\\${filename}`;

        const formData = new FormData();
        formData.append("content", profileImage, filePath);

        const response = await api.post(
          `/v1/UserCustomer/UploadPhoto?userId=${user?.id}`,
          formData,
        );

        if (response.status === 200) {
          addToast({
            type: "success",
            title: t("common.info"),
            description: t("camera.photoUpdated"),
          });
          await updateUserStorage(response.data.data.fileName);
          setPreview("");
        } else {
          throw new Error("Upload failed");
        }
      } catch {
        addToast({
          type: "error",
          title: t("common.info"),
          description: t("camera.uploadFailed"),
        });
      }
    };

    const capture = useCallback(() => {
      if (!webcamRef.current) {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("camera.notFound"),
        });
        return;
      }

      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("camera.captureError"),
        });
        return;
      }

      const canvas = document.createElement("canvas");
      const image = new Image();

      image.onload = function () {
        canvas.width = image.width;
        canvas.height = image.height;

        const context = canvas.getContext("2d");
        if (context) {
          context.drawImage(image, 0, 0, image.width, image.height);
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], "capturaImage.png", {
                type: "image/png",
              });
              setProfileImage(file);
              setPreview(URL.createObjectURL(file));
              onImageChange();
            }
          }, "image/png");
        }
      };
      image.src = imageSrc;
      setIsCameraMode(false);

      return () => {
        if (preview) URL.revokeObjectURL(preview);
      };
    }, [addToast, onImageChange]);

    const ALLOWED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg"];

    const updateFotoByComputer = (file: File) => {
      const extension = file.name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0];

      if (
        !["image/png", "image/jpeg"].includes(file.type) ||
        !extension ||
        !ALLOWED_IMAGE_EXTENSIONS.includes(extension)
      ) {
        addToast({
          type: "error",
          title: t("common.warning"),
          description: t("camera.invalidImageFile"),
        });
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      setProfileImage(file);
      setShowModalPicture(false);
      onImageChange();
    };

    useImperativeHandle(ref, () => ({
      handleUploadImage,
    }));

    async function hasAvailableCamera() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === "videoinput");
    }

    const permissionCamera = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        return true;
      } catch (error) {
        return false;
      }
    };

    const handleModalTakePicture = async () => {
      if (preview) {
        setProfileImage(null);
        setPreview("");
        setShowModalPicture(false);
      } else {
        if ((await hasAvailableCamera()) === false || (await permissionCamera()) === false) {
          addToast({
            type: "warning",
            title: t("common.warning"),
            description: t("camera.notFound"),
          });
          return;
        }
        setIsCameraMode(true);
        setShowModalPicture(false);
      }
    };

    useEffect(() => {
      return () => {
        if (preview) {
          URL.revokeObjectURL(preview);
        }
      };
    }, [preview]);

    const renderDefaultView = () => {
      return (
        <>
          <WebCamContainer>
            {preview ? (
              <img src={preview} alt="Preview" className="camera-img" />
            ) : user?.picture ? (
              <img src={`data:image/png;base64,${user.picture}`} alt="User" />
            ) : (
              <Avatar name={user?.name || "Avatar"} size="150" round />
            )}
          </WebCamContainer>

          <ButtonTakePhoto>
            <img
              src={CameraIcon}
              alt="Open camera options"
              onClick={() => setShowModalPicture(true)}
              className="camera-icon"
            />
          </ButtonTakePhoto>
        </>
      );
    };

    const renderCameraView = () => (
      <>
        <WebCamContainer>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotQuality={1}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              ...videoConstraints,
              deviceId: selectedVideo?.id,
            }}
            className="camera-img"
          />
        </WebCamContainer>
        <ButtonTakePhoto>
          <img
            src={CameraIcon}
            alt="Capture"
            onClick={capture}
            className="camera-icon"
          />
        </ButtonTakePhoto>
      </>
    );

    return (
      <BoxWebCamImage>
        {isCameraMode ? renderCameraView() : renderDefaultView()}
        {showModalPicture && (
          <ModalSelectOrTakePicture
            preview={preview}
            takePicture={handleModalTakePicture}
            onClose={() => setShowModalPicture(false)}
            setFileImg={updateFotoByComputer}
          />
        )}
      </BoxWebCamImage>
    );
  },
);

export default PhotoWebCam;
